// The Journal's gallery view — the entries as pictures rather than as prose.
//
// Two decisions worth stating, because both depart from the feed next door:
//
// No day headers. The feed groups by day and the grouping earns its place there, where every row is
// full width and a heading costs one line. In a three-across grid most days would open a new row for
// one or two tiles and the grid would come apart into ragged stripes. The date moves onto the card
// instead, which is also what the reference layout does.
//
// One control per tile. The reference shows a favourite star and an overflow menu on every card;
// this app has neither concept, and a button inside a button is invalid anyway. A tile is a single
// button that opens the entry, exactly like a feed row.

import { useRef } from 'react';
import { entryPreview } from '../entries/preview';
import { EntryArt, useNearbyPhoto } from '../entries/EntryArt';
import { timeLabel } from './home-support';
import { shortDate } from '../format/dates';
import type { DreamType, Entry } from '../api/entries';

const DREAM_TYPE_LABEL: Record<DreamType, string> = {
  normal: 'Dream',
  lucid: 'Lucid',
  nightmare: 'Nightmare',
};

export function JournalGallery({
  entries,
  day,
  onClearDay,
  onOpen,
}: {
  entries: Entry[];
  /** The `day` URL parameter, when one is narrowing the gallery. */
  day?: string | null;
  onClearDay?: () => void;
  onOpen: (id: string) => void;
}) {
  return (
    <>
      <div className="ogal-count">
        <span>
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </span>
        {/* A day can arrive from the calendar or straight from a shared link, and in the grid there
            is no calendar on screen to explain why two thirds of the entries are missing. */}
        {day ? (
          <button className="ogal-clear" onClick={onClearDay}>
            {dayLabel(day)}
            <span aria-hidden="true">×</span>
            <span className="osr-only">— show every day</span>
          </button>
        ) : null}
      </div>

      <div className="ogal">
        {entries.map((entry) => (
          <GalleryTile key={entry.id} entry={entry} onOpen={() => onOpen(entry.id)} />
        ))}
      </div>
    </>
  );
}

function GalleryTile({ entry, onOpen }: { entry: Entry; onOpen: () => void }) {
  const tile = useRef<HTMLButtonElement>(null);
  const photo = useNearbyPhoto(entry.id, tile);
  const preview = entryPreview(entry, photo?.url, photo?.crop);

  const isDream = entry.kind === 'dream';
  const tag = isDream ? (DREAM_TYPE_LABEL[entry.dreamType ?? 'normal'] ?? 'Dream') : 'Waking';

  return (
    <button ref={tile} className="ogal-tile" onClick={onOpen}>
      <EntryArt preview={preview} className="ogal-art" />
      <span className="ogal-meta">
        <span className="ogal-when">
          <span className="d">{shortDate(entry.createdAt)}</span>
          <span aria-hidden="true">·</span>
          <span className="t">{timeLabel(entry.createdAt)}</span>
        </span>
        <span className="ogal-title">{entry.title || 'Untitled'}</span>
        <span className={`ogal-tag ${isDream ? 'dream' : 'waking'}`}>
          <span className="dot" />
          {tag}
        </span>
      </span>
    </button>
  );
}

/** "2026-08-08" → "Sat, Aug 8, 2026". The key is built by `dayKey`, so it is always local and well-formed. */
function dayLabel(key: string): string {
  const [year, month, day] = key.split('-').map(Number);
  if (!year || !month || !day) return key;
  return shortDate(new Date(year, month - 1, day));
}
